import "./AddPageForCustomersModal.scss";

import React, { useState } from "react";

import {
  Alert,
  Button,
  Input,
  message,
  Modal,
  Radio,
  Space,
  Typography,
} from "antd";
import PropTypes from "prop-types";

import { useAppTheme } from "../../context/ThemeProvider";
import TemplatesAPI from "../../Pages/api/TemplatesAPI";
import { showAppError } from "../../Utils/error";
import { getSingularOrPlural } from "../../Utils/utilityFunctions";
import UploadUsersCSV from "./UploadUsersCSV";
import UsersTable from "./UsersTable";

const AddPageForCustomersModal = ({ open, onClose, onSuccess }) => {
  const { COLORS } = useAppTheme();

  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [pageType, setPageType] = useState("mango-page");
  const [pageId, setPageId] = useState("");
  const [csvUploadSuccess, setCsvUploadSuccess] = useState(0);
  const [csvUploadFailed, setCsvUploadFailed] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const resetModal = () => {
    setCsvFile(null);
    setCsvData([]);
    setPageId("");
    setCsvUploadSuccess(0);
    setCsvUploadFailed(0);
  };

  const handleCancel = () => {
    onClose();
    resetModal();
  };

  const handleCsvUpload = (uploadedFile, data, successCount, failedCount) => {
    setCsvFile(uploadedFile);
    setCsvData(data);
    setCsvUploadSuccess(successCount);
    setCsvUploadFailed(failedCount);
  };

  const handleChangeCsv = () => {
    resetModal();
  };

  const handleSave = async () => {
    if (!pageId.trim()) {
      message.error("Please enter a page ID");
      return;
    }

    if (!/^[0-9a-fA-F]{24}$/.test(pageId)) {
      message.error("Invalid page ID. Please check and try again.");
      return;
    }

    const validUsers = csvData.filter((user) => user.status === "valid");

    if (validUsers.length === 0) {
      message.error("No valid users to process");
      return;
    }

    setIsSaving(true);

    try {
      const resp = await TemplatesAPI.duplicateLandingPageForMultipleCreators({
        pageType,
        pageId: pageId.trim(),
        list: validUsers.map((user) => ({
          email: user.email,
          host: user.host,
        })),
      });

      if (resp?.code === 0) {
        message.success(
          `Successfully added page for ${
            validUsers.length
          } ${getSingularOrPlural(validUsers.length, "user")}`
        );
        onSuccess?.(validUsers.length);
        handleCancel();
      } else {
        showAppError(resp);
      }
    } catch (error) {
      showAppError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const renderContent = () => {
    if (!csvFile) {
      return (
        <UploadUsersCSV
          onUpload={handleCsvUpload}
          onError={() =>
            message.error("Wrong CSV format. Please check and try again.")
          }
        />
      );
    }

    return (
      <div className="csv-upload-success">
        <div className="csv-success-header">
          <Typography.Title level={4}>
            CSV uploaded successfully
          </Typography.Title>
          <Button
            type="link"
            onClick={handleChangeCsv}
            style={{ color: COLORS.INFO }}
          >
            Change CSV
          </Button>
        </div>

        <div className="upload-stats">
          {csvUploadSuccess > 0 && (
            <Alert
              message={`${csvUploadSuccess} ${getSingularOrPlural(
                csvUploadSuccess,
                "user"
              )} successfully imported`}
              type="success"
              style={{ marginBottom: 8 }}
            />
          )}

          {csvUploadFailed > 0 && (
            <Alert
              message={`${csvUploadFailed} ${getSingularOrPlural(
                csvUploadFailed,
                "user"
              )} failed to import (check table for errors)`}
              type="warning"
              style={{ marginBottom: 8 }}
            />
          )}
        </div>

        <div className="add-page-form__item page-type-options">
          <Typography.Paragraph strong style={{ marginBottom: 8 }}>
            Page type
          </Typography.Paragraph>
          <Radio.Group
            value={pageType}
            onChange={(e) => setPageType(e.target.value)}
          >
            <Space direction="horizontal">
              <Radio value="mango-page">Service page</Radio>
              <Radio value="website">Website</Radio>
            </Space>
          </Radio.Group>
        </div>

        <div className="add-page-form__item page-id-input">
          <Typography.Text strong>Page ID</Typography.Text>
          <Input
            size="large"
            placeholder="Add Page ID here"
            value={pageId}
            onChange={(e) => setPageId(e.target.value)}
          />
        </div>

        <UsersTable data={csvData} />
      </div>
    );
  };

  return (
    <Modal
      title="Add Page for Users"
      visible={open}
      open={open}
      onCancel={handleCancel}
      width={1024}
      className="add-page-customers-modal"
      centered
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={isSaving}
          disabled={csvData.length === 0 || !pageId.trim() || !pageType}
          onClick={handleSave}
        >
          Save
        </Button>,
      ]}
      destroyOnClose
    >
      <div className="modal-content-wrapper">{renderContent()}</div>
    </Modal>
  );
};

AddPageForCustomersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default AddPageForCustomersModal;
